import React, { Fragment, useEffect, useState } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureDownloadCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './ExpensesMain.css';

/* ---------- Inline SVG icons (no FontAwesome dependency) ---------- */

const BACK_ICON = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="19" y1="12" x2="5" y2="12"/>
        <polyline points="12 19 5 12 12 5"/>
    </svg>
);

const SEARCH_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

const RECEIPT_ICON = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2Z"/>
        <path d="M8 8h8"/><path d="M8 12h8"/><path d="M8 16h5"/>
    </svg>
);

const CHECK_ICON = (
    <svg className="ex-bullet-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const SPARKLES_ICON = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/>
    </svg>
);

const BUILDING_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="2" width="16" height="20" rx="2"/>
        <path d="M9 22v-4h6v4"/>
        <path d="M8 6h.01"/><path d="M12 6h.01"/><path d="M16 6h.01"/>
        <path d="M8 10h.01"/><path d="M12 10h.01"/><path d="M16 10h.01"/>
        <path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/>
    </svg>
);

const BOX_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
        <path d="m3.3 7 8.7 5 8.7-5"/>
        <path d="M12 22V12"/>
    </svg>
);

const CAR_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
        <circle cx="7" cy="17" r="2"/>
        <path d="M9 17h6"/>
        <circle cx="17" cy="17" r="2"/>
    </svg>
);

const WRENCH_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z"/>
    </svg>
);

const USERS_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);

const PHONE_ITEMS = [
    { labelKey: 'phoneItem1Label', valueKey: 'phoneItem1Value' },
    { labelKey: 'phoneItem2Label', subKey: 'phoneItem2Sub', valueKey: 'phoneItem2Value' },
    { labelKey: 'phoneItem3Label', subKey: 'phoneItem3Sub', valueKey: 'phoneItem3Value' },
    { labelKey: 'phoneItem4Label', subKey: 'phoneItem4Sub', valueKey: 'phoneItem4Value' },
    { labelKey: 'phoneItem5Label', subKey: 'phoneItem5Sub', valueKey: 'phoneItem5Value' },
    { labelKey: 'phoneItem6Label', subKey: 'phoneItem6Sub', valueKey: 'phoneItem6Value' },
    { labelKey: 'phoneItem7Label', subKey: 'phoneItem7Sub', valueKey: 'phoneItem7Value' },
    { labelKey: 'phoneItem8Label', subKey: 'phoneItem8Sub', valueKey: 'phoneItem8Value' }
];

const PROJECT_CHIPS = [
    { icon: BOX_ICON, labelKey: 'projectMaterialsChip' },
    { icon: CAR_ICON, labelKey: 'projectTravelChip' },
    { icon: WRENCH_ICON, labelKey: 'projectEquipmentChip' },
    { icon: USERS_ICON, labelKey: 'projectSubcontractorsChip' }
];

const RECEIPT_LINES = [
    { labelKey: 'receiptLine1Label', valueKey: 'receiptLine1Value' },
    { labelKey: 'receiptLine2Label', valueKey: 'receiptLine2Value' },
    { labelKey: 'receiptLine3Label', valueKey: 'receiptLine3Value' }
];

function ExpensesMain(){
    const { t, locale } = useLocale();
    const { formatCurrency } = useCurrency();
    const p = (key) => t(`featurePages.expenses.${key}`);

    const [loader, setLoader] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoader(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    if (!loader) {
        return <Loader/>;
    }

    const visionBullets = [p('visionBullet1'), p('visionBullet2')];

    const projectRows = [
        { label: p('projectMaterialsLabel'), value: formatCurrency(p('projectMaterialsValue')) },
        { label: p('projectTravelLabel'), value: formatCurrency(p('projectTravelValue')) },
        { label: p('projectOtherLabel'), value: formatCurrency(p('projectOtherValue')) }
    ];

    const heroVisual = (
        <div className="ex-phone" aria-hidden="true">
            <div className="ex-phone-header">
                <div className="ex-phone-toprow">
                    <span className="ex-phone-back">{BACK_ICON}</span>
                    <span className="ex-phone-add">{p('phoneAdd')}</span>
                </div>

                <h2 className="ex-phone-title">{p('phoneTitle')}</h2>

                <div className="ex-phone-search">
                    {SEARCH_ICON}
                    <span className="ex-phone-search-placeholder">{p('phoneSearchPlaceholder')}</span>
                </div>
            </div>

            <div className="ex-phone-list">
                {PHONE_ITEMS.map((item) => (
                    <div className="ex-phone-item" key={item.labelKey}>
                        <div className="ex-phone-item-icon">{RECEIPT_ICON}</div>
                        <div className="ex-phone-item-text">
                            <span className="ex-phone-item-label">{p(item.labelKey)}</span>
                            {item.subKey && (
                                <span className="ex-phone-item-sub">{p(item.subKey)}</span>
                            )}
                        </div>
                        <span className="ex-phone-item-value">{formatCurrency(p(item.valueKey))}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.expenses} locale={locale} />

            <FeatureHero
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="feature-content"
                    />
                }
                image={heroVisual}
            />

            {/* AI receipt scanning */}
            <section className="ex-vision-section">
                <div className="container">
                    <div className="ex-vision-grid">
                        <div className="ex-vision-text">
                            <span className="ex-vision-badge">
                                {SPARKLES_ICON}
                                <span>{p('visionBadge')}</span>
                            </span>
                            <div className="ex-vision-heading">
                                <h2 className="ex-section-title">{p('visionTitle')}</h2>
                                <span className="ex-vision-coming-soon">{t('placeholder.badge')}</span>
                            </div>
                            <p className="ex-section-desc">{p('visionDescription')}</p>
                            <ul className="ex-bullets">
                                {visionBullets.map((bullet, idx) => (
                                    <li className="ex-bullet" key={idx}>
                                        <span className="ex-bullet-tick">{CHECK_ICON}</span>
                                        <span>{bullet}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="ex-scan-stage">
                            <div className="ex-receipt">
                                <div className="ex-receipt-vendor">{p('receiptVendor')}</div>
                                {RECEIPT_LINES.map((line) => (
                                    <div className="ex-receipt-row" key={line.labelKey}>
                                        <span>{p(line.labelKey)}</span>
                                        <span>{formatCurrency(p(line.valueKey))}</span>
                                    </div>
                                ))}
                                <div className="ex-receipt-divider"></div>
                                <div className="ex-receipt-total">
                                    <span>{p('receiptTotalLabel')}</span>
                                    <span>{formatCurrency(p('receiptTotalValue'))}</span>
                                </div>
                                <div className="ex-scan-line"></div>
                            </div>

                            <div className="ex-scan-popup ex-scan-popup-vendor">
                                <span className="ex-scan-popup-label">{p('visionVendorLabel')}</span>
                                <span className="ex-scan-popup-value">
                                    {BUILDING_ICON}
                                    <span>{p('visionVendorValue')}</span>
                                </span>
                            </div>

                            <div className="ex-scan-popup ex-scan-popup-total">
                                <span className="ex-scan-popup-label">{p('visionTotalLabel')}</span>
                                <span className="ex-scan-popup-total-value">{formatCurrency(p('visionTotalValue'))}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div id="feature-content"></div>

            {/* Project expense tracking */}
            <section className="ex-project-section">
                <div className="container">
                    <div className="ex-project-grid">
                        <div className="ex-project-card">
                            <h3 className="ex-project-card-title">{p('projectCardTitle')}</h3>
                            <div className="ex-project-rows">
                                {projectRows.map((row) => (
                                    <div className="ex-project-row" key={row.label}>
                                        <span className="ex-project-row-label">{row.label}</span>
                                        <span className="ex-project-row-value">{row.value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="ex-project-total">
                                <span className="ex-project-total-label">{p('projectTotalLabel')}</span>
                                <span className="ex-project-total-value">{formatCurrency(p('projectTotalValue'))}</span>
                            </div>
                        </div>

                        <div className="ex-project-text">
                            <h2 className="ex-section-title">{p('projectTitle')}</h2>
                            <p className="ex-section-desc">{p('projectDescription')}</p>
                            <div className="ex-project-chips">
                                {PROJECT_CHIPS.map((chip) => (
                                    <div className="ex-project-chip" key={chip.labelKey}>
                                        <span className="ex-project-chip-icon">{chip.icon}</span>
                                        <span>{p(chip.labelKey)}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="ex-project-note">{p('projectNote')}</p>
                        </div>
                    </div>
                </div>
            </section>

            <FeatureDownloadCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            <Footer />
        </Fragment>
    )
}

export default ExpensesMain;